// What each page is called and where it sits (ADR 0030): every route declares it in its route
// definition, `staticData: { crumb }`, and lib/navRules.test.ts fails on a route without one. The
// root layout resolves the declaration of the page on screen (RouteCrumbs) and hands it to the
// trail; the shell draws the crumbs (PageCrumbs), Back and the sidebar highlight from it.

import { useMatches, useRouter } from "@tanstack/react-router";
import { type CrumbContext, type CrumbDecl, type TrailLevel, useCrumbDecl } from "@vd/ui";
import { useHereHref, usePageInfo } from "./navTrail";

declare module "@tanstack/react-router" {
  interface StaticDataRouteOption {
    /** The page's crumb: its name and its natural parents (ADR 0030). */
    crumb?: CrumbDecl;
  }
}

export type { CrumbContext, CrumbDecl, TrailLevel };

/** A level to a tab's page or a record: `level("Lists", "/lists")`. */
export const level = (label: string, href: string): TrailLevel => ({ label, href });

/** The declaration of the page on screen: its leaf route's, else the nearest one above it. */
function useLeafDecl(): { id: string; decl: CrumbDecl; ctx: CrumbContext } | null {
  const router = useRouter();
  const matches = useMatches();
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const m = matches[i];
    if (!m) continue;
    const route = (router.routesById as unknown as Record<string, { options?: { staticData?: unknown } }>)[
      m.routeId
    ];
    const decl = (route?.options?.staticData as { crumb?: CrumbDecl } | undefined)?.crumb;
    if (decl) {
      return {
        id: m.routeId,
        decl,
        ctx: {
          params: (m.params ?? {}) as Record<string, string>,
          search: (m.search ?? {}) as Record<string, unknown>,
        },
      };
    }
  }
  return null;
}

function Resolve({ decl, ctx }: { decl: CrumbDecl; ctx: CrumbContext }) {
  const href = useHereHref();
  usePageInfo(href, useCrumbDecl(decl, ctx));
  return null;
}

/** In the root layout: names the page on screen for the crumbs, Back and the sidebar. */
export function RouteCrumbs() {
  const leaf = useLeafDecl();
  if (!leaf) return null;
  return <Resolve key={leaf.id} decl={leaf.decl} ctx={leaf.ctx} />;
}
